import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useI18n } from '@i18n';
import {
  consumption_frequency_type_enum,
  DataCollectionAreaConsumptionDraftFragment,
  DataCollectionInsertConsumptionMutationVariables,
  ManualConsumptionSourceTypeEnum,
} from '@predium/client-graphql';
import { convertStandardUnitsToUserInput } from '@predium/client-lookup';
import {
  consumption_source_type_enum,
  consumption_sub_type_enum,
  consumption_type_enum,
  consumption_unit_enum,
  energy_source_type_enum,
} from '@predium/enums';
import { accessEnum } from '@predium/utils';
import { useSnackbar } from 'notistack';
import { ReactElement, useEffect } from 'react';
import { FieldErrorsImpl, useForm } from 'react-hook-form';
import { useSession } from '@/components/session/useSession';
import { FormProvider } from '../../../../components/hook-form';
import { SnackbarTimeouts } from '../../../../components/NotistackProvider';
import {
  DELETE_SINGLE_WASTE_DETAILS_DRAFT,
  INSERT_SINGLE_CONSUMPTION_DRAFT,
  INSERT_SINGLE_WASTE_DETAILS_DRAFT,
  UPDATE_SINGLE_CONSUMPTION_DRAFT,
  UPDATE_SINGLE_WASTE_DETAILS_DRAFT,
} from '../../../../graphql/DataCollection.mutations';
import { GET_BUILDING, GET_CONSUMPTION_DRAFTS } from '../../../../graphql/DataCollection.queries';
import usePosthogTracking from '../../../../hooks/usePosthogTracking';
import {
  constructConsumptionValidationSchema,
  ConsumptionWasteDetailsFormData,
  SingleConsumptionFormData,
} from './AreaSingleConsumptionForm';

export type SingleConsumptionDraftFormData = {
  // If undefined the draft doesn't exist yet,
  // id?: number;
  buildingId: number;

  consumptionDrafts: {
    // TRASH CONSUMPTION ONLY
    waste?: Partial<ConsumptionWasteDetailsFormData>;
  } & Partial<SingleConsumptionFormData['consumptionDrafts'][number]>[];
} & Omit<Partial<SingleConsumptionFormData>, 'buildingId' | 'areaId'> & {
    areaIds?: number[];
  };

type SingleConsumptionDraftFormValueProps = SingleConsumptionDraftFormData;

type ManualEntryConsumptionData = {
  type: 'manual';
  sourceTypeId: ManualConsumptionSourceTypeEnum;
  from: string;
  to: string;
  provider: string;
  areaIds: number[];
  consumptionTypeId: consumption_type_enum;
  energySourceTypeId?: energy_source_type_enum | null;
  subTypeId?: consumption_sub_type_enum | null;
  value?: number | null;
  displayUnitValue?: consumption_unit_enum | null;
  cost?: number | null;
  waste?: ConsumptionWasteDetailsFormData;
};

type DraftConsumptionData = {
  type: 'draft';
  variables: DataCollectionInsertConsumptionMutationVariables;
};

export type OnCreateSingleConsumption = (
  data: ManualEntryConsumptionData | DraftConsumptionData,
) => void | Promise<void>;

/** Seeds a brand-new draft; ignored once `consumptionDraftData` exists, whose own values win. */
export type SingleConsumptionPrefill = {
  areaIds?: number[];
  consumptionTypeId?: consumption_type_enum;
};

type Props = {
  buildingId: number;
  sourceTypeId: consumption_source_type_enum;
  prefill?: SingleConsumptionPrefill;
  consumptionDraftData: DataCollectionAreaConsumptionDraftFragment | null;
  setConsumptionDraftData: React.Dispatch<React.SetStateAction<DataCollectionAreaConsumptionDraftFragment | null>>;
  /**
   * Lifted state to determine if the child form is dirty
   */
  setIsChildFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateSingleConsumption: OnCreateSingleConsumption;
  renderChildren: (
    submitForm: () => Promise<DataCollectionInsertConsumptionMutationVariables>,
    errors: Partial<FieldErrorsImpl<SingleConsumptionDraftFormValueProps>>,
  ) => ReactElement;
};

function AreaSingleConsumptionDraftForm({
  buildingId,
  sourceTypeId,
  prefill,
  consumptionDraftData,
  setConsumptionDraftData,
  setIsChildFormDirty,
  onCreateSingleConsumption,
  renderChildren,
}: Props) {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();

  const { user, org } = useSession();
  const { trackEvent } = usePosthogTracking();

  const [createConsumptionDraft] = useMutation(INSERT_SINGLE_CONSUMPTION_DRAFT, {
    onError: () => {
      enqueueSnackbar(t('DataCollectionAreaConsumptionDraft_SaveChanges-error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    onCompleted: () => {
      enqueueSnackbar(t('General_ChangesSaved'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    },
    refetchQueries: [
      { query: GET_BUILDING, variables: { buildingId } },
      { query: GET_CONSUMPTION_DRAFTS, variables: { buildingId } },
    ],
  });
  const [createWasteDetailsDraft] = useMutation(INSERT_SINGLE_WASTE_DETAILS_DRAFT);
  const [saveConsumptionDraft] = useMutation(UPDATE_SINGLE_CONSUMPTION_DRAFT, {
    onError: () => {
      enqueueSnackbar(t('DataCollectionAreaConsumptionDraft_SaveChanges-error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    onCompleted: () => {
      enqueueSnackbar(t('General_ChangesSaved'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    },
    refetchQueries: [{ query: GET_CONSUMPTION_DRAFTS, variables: { buildingId } }],
  });
  const [updateWasteDetailsDraft] = useMutation(UPDATE_SINGLE_WASTE_DETAILS_DRAFT);
  const [removeWasteDetailsDraft] = useMutation(DELETE_SINGLE_WASTE_DETAILS_DRAFT, {
    refetchQueries: [{ query: GET_CONSUMPTION_DRAFTS, variables: { buildingId } }],
  });

  const DataSchema = constructConsumptionValidationSchema({});

  const {
    id,
    from,
    to,
    provider,
    consumption_type_id,
    cost,
    energy_source_type_id,
    sub_type_id,
    displayValue,
    display_unit_value,
    consumption_waste_details_draft,
    consumption_draft_areas,
  } = consumptionDraftData ?? {};

  const areaIds = consumption_draft_areas?.map((area) => area.area_id) ?? prefill?.areaIds;

  const methods = useForm<SingleConsumptionDraftFormValueProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    resolver: yupResolver(DataSchema),
    shouldFocusError: false,
    defaultValues: {
      id,
      buildingId,

      provider: provider ?? '',
      from: from ?? '',
      to: to ?? '',
      areaIds: areaIds,

      consumptionDrafts: [
        {
          id,
          consumptionTypeId: consumption_type_id ?? prefill?.consumptionTypeId,
          energySourceTypeId: energy_source_type_id ?? undefined,
          subTypeId: sub_type_id ?? undefined,
          value: displayValue ?? undefined,
          displayUnitValue: display_unit_value ?? ('' as consumption_unit_enum),
          cost: cost ?? undefined,

          // TRASH CONSUMPTION ONLY, OPTIONAL
          waste: consumption_waste_details_draft
            ? {
                id: consumption_waste_details_draft.id ?? undefined,
                volume:
                  consumption_waste_details_draft.volume && consumption_waste_details_draft.display_unit_volume
                    ? parseFloat(
                        convertStandardUnitsToUserInput(
                          consumption_type_id!,
                          consumption_waste_details_draft.display_unit_volume,
                          consumption_waste_details_draft.volume,
                          (energy_source_type_id ?? sub_type_id)!,
                        ).toFixed(4),
                      )
                    : (consumption_waste_details_draft.volume ?? undefined),
                displayUnitVolume: consumption_waste_details_draft.display_unit_volume ?? ('' as consumption_unit_enum),
                frequency:
                  consumption_waste_details_draft.frequency && consumption_waste_details_draft.display_unit_frequency
                    ? parseFloat(
                        convertStandardUnitsToUserInput(
                          consumption_type_id!,
                          consumption_waste_details_draft.display_unit_frequency,
                          consumption_waste_details_draft.frequency,
                          (energy_source_type_id ?? sub_type_id)!,
                        ).toFixed(4),
                      )
                    : (consumption_waste_details_draft.frequency ?? undefined),
                displayUnitFrequency:
                  consumption_waste_details_draft.display_unit_frequency ?? ('' as consumption_frequency_type_enum),
                amount: consumption_waste_details_draft.amount ?? undefined,
                price: consumption_waste_details_draft.price ?? undefined,
              }
            : undefined,
        },
      ],
    },
  });

  const {
    handleSubmit,
    getValues,
    formState: { errors, isDirty },
    setValue,
  } = methods;

  useEffect(() => {
    setIsChildFormDirty(isDirty);
  }, [isDirty, setIsChildFormDirty]);

  /**
   *
   * @param data The forms native data after validation passes
   *
   * The following process is done for inserting a new consumption draft or analog to updating an existing draft.
   *
   * 1. If there is no draft data, create a new draft
   * 2. If there is draft data, update the draft
   * 3. If waste details are set create them if they don't exist, update them if they do
   * 4. Reset form with values from the network result
   * 5. Return the data to potentially create the real consumption
   *
   */
  const saveForm = async (
    data: SingleConsumptionDraftFormValueProps,
  ): Promise<DataCollectionInsertConsumptionMutationVariables> => {
    const { from, to, provider, consumptionDrafts, areaIds } = data;

    const { consumptionTypeId, energySourceTypeId, subTypeId, value, displayUnitValue, cost, waste } =
      consumptionDrafts[0];

    // We use this to return the data to create the consumption and reset the form for either INSERT or UPDATE
    let returnData: (DataCollectionInsertConsumptionMutationVariables & { consumptionDraftId: number }) | undefined =
      undefined;

    let wasteId: number | undefined = undefined;

    // 1. If there is no draft data, create a new draft
    if (!data.id) {
      const { data: mutationResult } = await createConsumptionDraft({
        variables: {
          buildingId,
          areaIds,
          // form control defaults to "" but we want to keep it null instead
          from: from || undefined,
          to: to || undefined,
          value,
          displayUnitValue: displayUnitValue || undefined,
          cost,
          provider: provider || undefined,
          sourceTypeId,
          consumptionTypeId,
          energySourceTypeId,
          subTypeId,
          userId: user.id,
        },
      });

      const {
        id,
        created_by_user_id,
        source_type_id,
        consumption_type_id,
        energy_source_type_id,
        sub_type_id,
        from: fromAfterInsert,
        to: toAfterInsert,
        displayValue: displayValueAfterInsert,
        display_unit_value: displayUnitValueAfterInsert,
        cost: costAfterInsert,
        provider: providerAfterInsert,
      } = mutationResult!.createConsumptionDraft!.consumption_draft!;

      returnData = {
        consumptionDraftId: id,
        buildingId,
        areaIds: areaIds ?? [],
        sourceTypeId: source_type_id,
        consumptionTypeId: consumption_type_id!,
        energySourceTypeId: energy_source_type_id!,
        subTypeId: sub_type_id!,
        createdByUserId: created_by_user_id!,
        from: fromAfterInsert as string,
        to: toAfterInsert!,
        /*
          The returnData is used to create the consumption, so we need to use the displayValue instead of the value here 
          as we were initializing the form value with the displayValue.
          Without this, the consumption will be created with the standard value without considering the displayUnitValue.
        */
        value: displayValueAfterInsert!,
        displayUnitValue: displayUnitValueAfterInsert!,
        cost: costAfterInsert!,
        provider: providerAfterInsert!,
      };
      setConsumptionDraftData({
        ...mutationResult!.createConsumptionDraft!.consumption_draft!,
        consumption_draft_areas: consumption_draft_areas ?? [],
      });
    }

    // 2. If there is draft data, update the draft
    else {
      trackEvent('BUILDING_SINGLE_CONSUMPTION_DRAFT_UPDATED', {
        org: org.id,
        user: user.id,
        building_id: buildingId,
        draft_id: data.id,
      });
      const { data: mutationResult } = await saveConsumptionDraft({
        variables: {
          consumptionDraftId: data.id!,
          areaIds,
          from: from || undefined,
          to: to || undefined,
          value,
          displayUnitValue: displayUnitValue || undefined,
          cost,
          provider: provider || undefined,
          consumptionTypeId,
          energySourceTypeId,
          subTypeId,
        },
      });

      const {
        id,
        source_type_id,
        consumption_type_id,
        created_by_user_id,
        energy_source_type_id,
        sub_type_id,
        from: fromAfterUpdate,
        to: toAfterUpdate,
        displayValue: displayValueAfterUpdate,
        display_unit_value: displayUnitValueAfterUpdate,
        cost: costAfterUpdate,
        provider: providerAfterUpdate,
        consumption_waste_details_draft,
      } = mutationResult!.updateConsumptionDraft!.consumption_draft!;

      wasteId = consumption_waste_details_draft?.id;

      returnData = {
        consumptionDraftId: id,
        buildingId,
        areaIds: areaIds ?? [],
        sourceTypeId: source_type_id,
        consumptionTypeId: consumption_type_id!,
        createdByUserId: created_by_user_id!,
        from: fromAfterUpdate!,
        to: toAfterUpdate!,
        value: displayValueAfterUpdate!,
        displayUnitValue: displayUnitValueAfterUpdate!,
        cost: costAfterUpdate!,
        provider: providerAfterUpdate!,
        energySourceTypeId: energy_source_type_id!,
        subTypeId: sub_type_id!,
      };
    }

    if (!returnData) {
      throw new Error('Failed to create or update consumption draft');
    }

    setValue('id', returnData.consumptionDraftId);

    if (wasteId && !waste) {
      await removeWasteDetailsDraft({
        variables: {
          id: wasteId,
        },
      });

      return returnData;
    }

    if (!wasteId && !waste) {
      return returnData;
    }

    if (!waste) {
      throw new Error('Waste details are missing');
    }

    const wasteObject = {
      volume: waste.volume,
      displayUnitVolume: waste.displayUnitVolume || undefined,
      frequency: waste.frequency,
      displayUnitFrequency: waste.displayUnitFrequency || undefined,
      amount: waste.amount,
      price: waste.price,
    };

    if (!wasteId && waste) {
      const { data: createWasteDetailsData } = await createWasteDetailsDraft({
        variables: {
          consumptionDraftId: returnData.consumptionDraftId,
          ...wasteObject,
        },
        refetchQueries: [{ query: GET_CONSUMPTION_DRAFTS, variables: { buildingId } }],
      });

      const wasteDetailsDraft = createWasteDetailsData?.createConsumptionWasteDetailsDraft;

      if (!wasteDetailsDraft) {
        throw new Error('Failed to create waste details');
      }

      return returnData;
    }

    const { data: updateWasteDetailsData } = await updateWasteDetailsDraft({
      variables: {
        id: wasteId!,
        ...wasteObject,
      },
      refetchQueries: [{ query: GET_CONSUMPTION_DRAFTS, variables: { buildingId } }],
    });

    const wasteDetailsDraft = updateWasteDetailsData?.updateConsumptionWasteDetailsDraft;

    if (!wasteDetailsDraft) {
      throw new Error('Failed to update waste details');
    }

    return returnData;
  };

  // Called when just the draft should be saved
  const submitForm = async () => {
    const values = getValues();

    return await saveForm(values);
  };

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit(async (data) => {
        if (
          sourceTypeId === consumption_source_type_enum.MANUAL_ENTRY ||
          sourceTypeId === consumption_source_type_enum.ESTIMATION
        ) {
          const { from, to, provider, consumptionDrafts, areaIds } = data;
          const { consumptionTypeId, energySourceTypeId, subTypeId, value, displayUnitValue, cost, waste } =
            consumptionDrafts[0];

          if (!consumptionTypeId || !provider || !from || !to) {
            return;
          }

          onCreateSingleConsumption({
            type: 'manual',
            sourceTypeId: accessEnum(ManualConsumptionSourceTypeEnum, sourceTypeId),
            from: from as string,
            to: to as string,
            provider,
            areaIds: areaIds ?? [],
            consumptionTypeId,
            energySourceTypeId,
            subTypeId,
            value,
            displayUnitValue,
            cost,
            waste: waste as ConsumptionWasteDetailsFormData | undefined,
          });
        } else {
          const insertUpdateResponse = await saveForm(data);

          onCreateSingleConsumption({ type: 'draft', variables: insertUpdateResponse });
        }
      })}
    >
      {/* @ts-ignore */}
      {renderChildren(submitForm, errors)}
    </FormProvider>
  );
}

export default AreaSingleConsumptionDraftForm;
